import clsx from 'clsx';
import { memo } from 'react';
import { NavLink } from 'react-router';

import { tooltipConfig, TooltipWrap } from '@shared/ui/tooltip';

import s from './SidebarNavList.module.css';

const defaultGetLabelClassName = ({ isActive }) =>
	clsx(isActive ? 'body-semibold-base' : 'body-medium-base');

const SidebarNavListComponent = ({
	items = [],
	ariaLabel,
	isMini = false,
	buildPath,
	tooltipName,
	getItemKey,
	getLabelClassName = defaultGetLabelClassName,
	navClassName,
	listClassName,
	itemClassName,
	linkClassName,
	activeLinkClassName,
	listStyle,
}) => {
	if (!items.length) return null;

	const listClasses = clsx('sidebar-nav', listClassName);
	const itemClasses = clsx(s.item, isMini && s.itemMini, itemClassName);

	const getLinkClasses = ({ isActive }) =>
		clsx(
			'nav-item',
			'nav-item--sidebar',
			isMini && 'nav-item--mini',
			isActive && 'nav-item--active',
			linkClassName,
			isActive && activeLinkClassName,
		);

	const resolveItemKey = (item, index) => {
		if (getItemKey) return getItemKey(item, index);
		if (item.to) return item.to;
		if (item.label) return item.label;
		return index;
	};

	const resolvePath = (item) => {
		if (buildPath) return buildPath(item);
		return item.to;
	};

	const resolveTooltipProps = (label) => {
		if (!isMini || !tooltipName || !label) return null;
		const base = tooltipConfig[tooltipName];
		if (!base) return null;

		return {
			...base,
			title: label,
		};
	};

	return (
		<nav aria-label={ariaLabel} className={navClassName}>
			<ul className={listClasses} style={listStyle}>
				{items.map((item, index) => {
					const { label, icon: Icon, activeIcon: ActiveIcon, end = true } = item;
					const path = resolvePath(item);
					if (!path) return null;

					const tooltipProps = resolveTooltipProps(label);
					const key = resolveItemKey(item, index);

					const content = (
						<NavLink
							to={path}
							end={end}
							className={getLinkClasses}
							aria-label={isMini ? label : undefined}
						>
							{({ isActive }) => {
								const CurrentIcon = isActive && ActiveIcon ? ActiveIcon : Icon;
								const labelClassName = getLabelClassName({ isActive, item });
								return (
									<>
										{CurrentIcon && (
											<CurrentIcon
												className='nav-item__icon'
												aria-hidden='true'
												focusable='false'
											/>
										)}
										{!isMini ? (
											<span className={clsx(labelClassName, 'nav-item__title')}>{label}</span>
										) : null}
									</>
								);
							}}
						</NavLink>
					);

					return (
						<li key={key} className={itemClasses}>
							<TooltipWrap tooltipProps={tooltipProps}>{content}</TooltipWrap>
						</li>
					);
				})}
			</ul>
		</nav>
	);
};

export const SidebarNavList = memo(SidebarNavListComponent);
